import React, { Dispatch, SetStateAction, useState } from "react";
import {TFunction} from "i18next";
import {useTranslation} from "react-i18next";
import { TEAM_SIZE } from "common/const";
import Table, { TableProps } from "@awsui/components-react/table";
import Header from "@awsui/components-react/header";
import Competition from "model/Competition";
import { useQuery } from "react-query";
import useTable from "hooks/useTable";
import Pagination from "@awsui/components-react/pagination";
import TextFilter from "@awsui/components-react/text-filter";
import CollectionPreferences from "@awsui/components-react/collection-preferences";
import Rider from "model/Rider";
import getRiders from "api/getRiders";
import LineupsValue from "pages/lineup/components/LineupsValue";
import SubmitLineupsButton from "pages/lineup/components/SubmitLineupsButton";
import { IconProps } from "@awsui/components-react/icon/interfaces";
import CompetitionsHeader from "components/CompetitionsHeader";
import { useAuth } from "hooks/useAuth";
import FlagIcon from "components/icons/FlagIcon";
import {formatNumber} from "common/formatNumber";
import { FlashbarProps } from "@awsui/components-react";
import RiderPopover from "components/RiderPopover";


interface LineupsTableProps {
    competition: Competition;
    setSubmitMessage: Dispatch<SetStateAction<FlashbarProps.MessageDefinition | undefined>>;
}

const columnDefinitions = (t: TFunction, tCommon: TFunction): TableProps.ColumnDefinition<Rider>[] => [
    {
        id: "name",
        header: t("columns.name"),
        cell: (item: Rider) => <RiderPopover rider={item} />,
        sortingField: "name"
    },
    {
        id: "team",
        header: t("columns.team"),
        cell: (item: Rider) => item.team || "-",
        sortingField: "team"
    },
    {
        id: "country",
        header: t("columns.country"),
        cell: (item: Rider) => item.country && <FlagIcon country={item.country}/>,
        sortingField: "country"
    },
    {
        id: "value",
        header: t("columns.value"),
        cell: (item: Rider) => `${formatNumber(item.value, 1)} ${tCommon("common.million")}`,
        sortingField: "value"
    }
];

const LineupsTable: React.FC<LineupsTableProps> = (props) => {

    const {t} = useTranslation("lineup");
    const {t: tCommon} = useTranslation("common");
    const competition = props.competition;

    const [{ league, user }] = useAuth();

    // <LeagueRoute> wraps /lineup in App.tsx, so league is always defined here.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const leagueForApi = league!;

    const [buttonEnabled, setButtonEnabled] = useState(true);
    const [buttonIconName, setButtonIconName] = useState<IconProps.Name | undefined>();

    const {
        isLoading: ridersIsLoading,
        data: riders,
        error: getRidersError
    } = useQuery<Rider[], Error>(`getRiders-${competition.code}`, () => getRiders(competition, leagueForApi, user?.token), {
        onSuccess: (returnedRiders: Rider[]) => {
            selectRiders(returnedRiders.filter(rider => rider.selected));
        }
    });

    const {
        preferences,
        setPreferences,
        items,
        actions,
        collectionProps,
        filterProps,
        paginationProps,
        selectedItems,
        collectionPreferencesProps
    } = useTable(riders || [], {
        emptyStateSubtitle: getRidersError as Error,
        emptyStateTitle: t("empty"),
        pageSize: 50
    });

    const selectRiders = (riders: Rider[]) => {
        actions.setSelectedItems(riders);
        setButtonEnabled(true);
        setButtonIconName(undefined);
    };

    return (
        <Table
            {...collectionProps}
            loadingText={t("loading")}
            columnDefinitions={columnDefinitions(t, tCommon)}
            items={items}
            loading={ridersIsLoading}
            wrapLines={false}
            header={
                <Header
                    counter={
                        selectedItems.length
                            ? `(${selectedItems.length}/${TEAM_SIZE}) `
                            : `(0/${TEAM_SIZE}) `
                    }
                    info={
                        <LineupsValue selectedRiders={selectedItems} />
                    }
                    actions={
                        <SubmitLineupsButton
                            selectedRiders={selectedItems}
                            competition={competition}
                            league={leagueForApi}
                            buttonEnabled={buttonEnabled}
                            setButtonEnabled={setButtonEnabled}
                            buttonIconName={buttonIconName}
                            setButtonIconName={setButtonIconName}
                            setSubmitMessage={props.setSubmitMessage}
                        />
                    }
                >
                    <CompetitionsHeader competition={competition} />
                </Header>
            }
            selectionType="multi"
            onSelectionChange={({ detail }) => selectRiders(detail.selectedItems as Rider[])}
            selectedItems={selectedItems}
            pagination={
                <Pagination
                    {...paginationProps}
                    ariaLabels={{
                        nextPageLabel: tCommon("table.nextPage"),
                        previousPageLabel: tCommon("table.previousPage"),
                        pageLabel: pageNumber => tCommon("table.page", {page: pageNumber})
                    }}
                />
            }
            filter={
                <TextFilter
                    {...filterProps}
                    filteringPlaceholder={tCommon("table.filterPlaceholder")}
                />
            }
            preferences={
                <CollectionPreferences
                    {...collectionPreferencesProps}
                    preferences={preferences}
                    onConfirm={({ detail }) => setPreferences(detail)}
                />
            }
        />
    );
};

export default LineupsTable;
